import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUser, FaStore, FaHeart, FaCheckCircle, FaUserFriends } from 'react-icons/fa';
import { BiEdit } from 'react-icons/bi';
import { format } from 'date-fns';
import { es, enUS, id as idLocale } from 'date-fns/locale';
import StarRating from '../StarRating';
import VerificationBadge from '../VerificationBadge';
import FollowButton from '../FollowButton';
import OnlineStatus from '../OnlineStatus';
import useApi from '../../helpers/TerserahApi';
import './UserProfile.css';

const locales = { es, en: enUS, id: idLocale };

const UserProfile = ({ userId, isOwnProfile = false, userData = null }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const api = useApi();
  const [user, setUser] = useState(userData);
  const [stats, setStats] = useState(null);
  const [recentReviews, setRecentReviews] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(!userData);
  const [editingBio, setEditingBio] = useState(false);
  const [bioText, setBioText] = useState('');
  const [bioError, setBioError] = useState('');
  const [savingBio, setSavingBio] = useState(false);

  useEffect(() => {
    if (!userData && userId) {
      fetchUserProfile();
    } else if (userData) {
      setBioText(userData.bio || '');
    }
  }, [userId, userData]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      // Cliente API: adjunta el token desde cookies y refresca si caducó
      const data = await api.getUserProfile(userId);
      const profile = data?.user ?? data?.data?.user ?? data;
      const userObj = profile?.user ?? profile;

      if (!userObj) {
        throw new Error(t('error_loading_profile') || 'Error loading profile');
      }

      setUser(userObj);
      setStats(profile?.stats ?? data?.stats ?? null);
      setRecentReviews(profile?.recentReviews ?? data?.recentReviews ?? []);
      setIsFollowing(Boolean(profile?.isFollowing ?? data?.isFollowing));
      setBioText(userObj.bio || '');
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBio = async () => {
    if (bioText.length > 500) {
      setBioError(t('bio_max_length') || 'Bio must be less than 500 characters');
      return;
    }

    try {
      setSavingBio(true);
      setBioError('');
      
      const data = await api.updateBio(bioText);
      const savedBio = data?.bio ?? data?.user?.bio ?? bioText;
      setUser({ ...user, bio: savedBio });
      setEditingBio(false);
    } catch (error) {
      console.error('Error saving bio:', error);
      setBioError(t('error_saving_bio') || 'Error saving bio');
    } finally {
      setSavingBio(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const locale = locales[i18n.language] || locales.en;
    return format(date, 'PPP', { locale });
  };

  if (loading) {
    return (
      <div className="user-profile-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-profile-error">
        <p>{t('user_not_found') || 'User not found'}</p>
      </div>
    );
  }

  return (
    <motion.div 
      className="user-profile-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header Section */}
      <motion.div 
        className="user-profile-header"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <div className="user-profile-avatar">
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} loading="lazy" />
          ) : (
            <div className="user-profile-avatar-placeholder">
              <FaUser size={48} />
            </div>
          )}
          <OnlineStatus 
            isOnline={user.is_online} 
            lastSeen={user.last_seen} 
          />
        </div>
        
        <div className="user-profile-info">
          <div className="user-profile-name-row">
            <h1>{user.name}</h1>
            <VerificationBadge 
              phoneVerified={user.phone_verified}
              userType={user.user_type}
            />
          </div>
          
          {user.state && (
            <p className="user-profile-location">
              📍 {user.state}
            </p>
          )}
          
          {user.rating > 0 && (
            <div className="user-profile-rating">
              <StarRating rating={user.rating} size="small" />
              <span className="rating-text">
                {user.rating.toFixed(1)} ({user.review_count} {t('reviews')})
              </span>
            </div>
          )}
          
          {!isOwnProfile && (
            <div className="user-profile-actions">
              <FollowButton 
                userId={userId}
                isFollowing={isFollowing}
                onFollowChange={setIsFollowing}
              />
            </div>
          )}
        </div>
      </motion.div>

      {/* Bio Section */}
      <motion.div 
        className="user-profile-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <div className="section-header">
          <h2>{t('about')}</h2>
          {isOwnProfile && !editingBio && (
            <button 
              className="btn-edit-bio"
              onClick={() => setEditingBio(true)}
            >
              <BiEdit /> {t('edit')}
            </button>
          )}
        </div>
        
        {editingBio ? (
          <div className="bio-editor">
            <textarea
              value={bioText}
              onChange={(e) => setBioText(e.target.value)}
              placeholder={t('bio_placeholder') || 'Tell us about yourself...'}
              maxLength={500}
              rows={4}
            />
            <div className="bio-editor-footer">
              <span className={bioText.length > 500 ? 'char-count error' : 'char-count'}>
                {bioText.length}/500
              </span>
              <div className="bio-editor-buttons">
                <button 
                  className="btn-cancel"
                  onClick={() => {
                    setBioText(user.bio || '');
                    setEditingBio(false);
                    setBioError('');
                  }}
                  disabled={savingBio}
                >
                  {t('cancel')}
                </button>
                <button 
                  className="btn-save"
                  onClick={handleSaveBio}
                  disabled={savingBio || bioText.length > 500}
                >
                  {savingBio ? t('saving') : t('save')}
                </button>
              </div>
            </div>
            {bioError && <p className="bio-error">{bioError}</p>}
          </div>
        ) : (
          <p className="user-profile-bio">
            {user.bio || (isOwnProfile ? t('no_bio_yet') : t('no_bio'))}
          </p>
        )}
        
        <p className="user-profile-member-since">
          {t('member_since')}: {formatDate(user.created_at)}
        </p>
      </motion.div>

      {/* Stats Section */}
      {stats && (
        <motion.div 
          className="user-profile-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <h2>{t('statistics')}</h2>
          <div className="user-profile-stats">
            <div className="stat-item">
              <FaStore className="stat-icon" />
              <div className="stat-content">
                <span className="stat-value">{stats.activeAds}</span>
                <span className="stat-label">{t('active_ads')}</span>
              </div>
            </div>
            
            <div className="stat-item">
              <FaCheckCircle className="stat-icon" />
              <div className="stat-content">
                <span className="stat-value">{stats.soldAds}</span>
                <span className="stat-label">{t('sold_ads')}</span>
              </div>
            </div>
            
            <div className="stat-item">
              <FaUserFriends className="stat-icon" />
              <div className="stat-content">
                <span className="stat-value">{stats.followers}</span>
                <span className="stat-label">{t('followers')}</span>
              </div>
            </div>
            
            <div className="stat-item">
              <FaHeart className="stat-icon" />
              <div className="stat-content">
                <span className="stat-value">{stats.following}</span>
                <span className="stat-label">{t('following')}</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Recent Reviews Section */}
      {recentReviews.length > 0 && (
        <motion.div 
          className="user-profile-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <div className="section-header">
            <h2>{t('recent_reviews')}</h2>
            <button 
              className="btn-view-all"
              onClick={() => navigate(`/user/${userId}/reviews`)}
            >
              {t('view_all')}
            </button>
          </div>
          
          <div className="reviews-list">
            {recentReviews.map((review, index) => (
              <div key={index} className="review-item">
                <div className="review-header">
                  <div className="reviewer-info">
                    <strong>{review.reviewer_name}</strong>
                    <StarRating rating={review.rating} size="small" />
                  </div>
                  <span className="review-date">
                    {formatDate(review.created_at)}
                  </span>
                </div>
                <p className="review-comment">{review.comment}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default UserProfile;
